export const DIMENSIONS = [
  "decisiveness",
  "strategy",
  "idealism",
  "influence",
  "risk",
  "order",
] as const;

export type Dimension = (typeof DIMENSIONS)[number];
export type ScoreVector = Record<Dimension, number>;

export type QuestionOption = {
  id: string;
  text: string;
  scores: ScoreVector;
};

export type Question = {
  id: string;
  eyebrow: string;
  text: string;
  options: QuestionOption[];
};

export type FigureReport = {
  why: string;
  strengths: string;
  blindspots: string;
  decisions: string;
  relationships: string;
  pressure: string;
  environment: string;
  story: string;
  advice: string;
};

export type ReportEvidence = {
  scene: string;
  choice: string;
  insight: string;
};

export type PersonalizedReport = FigureReport & {
  contentVersion: string;
  archetypeName: string;
  title: string;
  subtitle: string;
  evidence: ReportEvidence[];
  secondaryComparison: string;
  actions: string[];
};

export type HistoricalFigure = {
  slug: string;
  name: string;
  era: string;
  role: string;
  monogram: string;
  color: string;
  labels: [string, string, string];
  tagline: string;
  summary: string;
  profile: ScoreVector;
  keyDimensions: Dimension[];
  priority: number;
};

export type PublicHistoricalFigure = Pick<
  HistoricalFigure,
  | "slug"
  | "name"
  | "era"
  | "role"
  | "monogram"
  | "color"
  | "labels"
  | "tagline"
  | "summary"
>;

export type AnswerMap = Record<string, string>;

export type RankedFigure = {
  slug: string;
  similarity: number;
  displayScore: number;
  keyDistance: number;
};

export type ScoredResult = {
  rawScores: ScoreVector;
  normalizedScores: ScoreVector;
  ranking: RankedFigure[];
  winner: RankedFigure;
};

export type PublicResult = {
  publicToken: string;
  figure: PublicHistoricalFigure;
  displayScore: number;
  normalizedScores: ScoreVector;
  topMatches: Array<{
    slug: string;
    name: string;
    displayScore: number;
  }>;
  unlocked: boolean;
  reportPreview: {
    archetypeName: string;
    title: string;
    preview: string;
    evidenceHint: string;
    secondaryName: string;
  };
  report?: PersonalizedReport;
  createdAt: string;
};
